// components/FAQ.js
import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaQuestionCircle, FaSolarPanel, FaMoneyBillWave, FaTools, FaShieldAlt } from 'react-icons/fa';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState([]);

  const toggleQuestion = (id) => {
    if (openQuestions.includes(id)) {
      setOpenQuestions(openQuestions.filter(qId => qId !== id));
    } else {
      setOpenQuestions([...openQuestions, id]);
    }
  };

  const faqData = {
    general: [
      {
        id: 'gen1',
        question: "What is solar energy and how does it work?",
        answer: "Solar energy is electricity generated from sunlight using photovoltaic (PV) panels. These panels contain solar cells that convert sunlight into direct current (DC) electricity, which is then converted to alternating current (AC) by an inverter for use in your home or business."
      },
      {
        id: 'gen2',
        question: "Why should I choose solar energy?",
        answer: "Solar energy reduces electricity bills, provides energy independence, has low maintenance costs, increases property value, and helps protect the environment by reducing carbon emissions. In Malawi, it's particularly beneficial due to abundant sunshine and frequent power outages."
      },
      {
        id: 'gen3',
        question: "How long do solar panels last?",
        answer: "Most solar panels come with a 25-30 year warranty and can continue producing electricity beyond that period. The efficiency may decrease slightly over time, but modern panels typically maintain 80-90% of their original capacity after 25 years."
      },
      {
        id: 'gen4',
        question: "Do solar panels work during cloudy days or at night?",
        answer: "Solar panels work during cloudy days but at reduced efficiency (typically 10-25% of their rated capacity). At night, they don't produce electricity, which is why most systems include battery storage or grid connection for continuous power supply."
      }
    ],
    installation: [
      {
        id: 'inst1',
        question: "How long does solar installation take?",
        answer: "Residential installations typically take 2-5 days, while commercial projects can take 2-8 weeks depending on system size. This includes site assessment, design approval, installation, and commissioning."
      },
      {
        id: 'inst2',
        question: "What are the installation requirements?",
        answer: "We need a structurally sound roof or ground space with good sun exposure (preferably south-facing in Malawi), proper electrical wiring, and necessary permits. Our team conducts a free site survey to determine feasibility."
      },
      {
        id: 'inst3',
        question: "Do I need to reinforce my roof?",
        answer: "Most roofs in Malawi are suitable for solar installation without reinforcement. Our engineers assess your roof structure during the site survey and will recommend any necessary modifications if needed."
      },
      {
        id: 'inst4',
        question: "Can I install solar panels myself?",
        answer: "We strongly recommend professional installation. Solar systems involve high-voltage electricity and complex electrical work. Professional installation ensures safety, warranty validity, and optimal system performance."
      }
    ],
    financial: [
      {
        id: 'fin1',
        question: "How much does a solar system cost?",
        answer: "Costs vary based on system size and complexity. Residential systems (3-10kW) typically range from MWK 2-8 million, while commercial systems (10-500kW) range from MWK 10-200 million. We provide detailed quotes after site assessment."
      },
      {
        id: 'fin2',
        question: "What is the return on investment (ROI) period?",
        answer: "Typically 3-7 years in Malawi, depending on your electricity consumption and current tariffs. After the ROI period, you'll enjoy essentially free electricity for the remaining system lifespan (20+ years)."
      },
      {
        id: 'fin3',
        question: "Do you offer financing options?",
        answer: "Yes, we partner with local financial institutions to provide flexible payment plans. We also help clients explore available government incentives and renewable energy financing programs."
      },
      {
        id: 'fin4',
        question: "Will solar increase my property value?",
        answer: "Yes, studies show solar installations can increase property value by 3-4% on average. Energy-efficient homes are increasingly attractive to buyers in Malawi's real estate market."
      }
    ],
    maintenance: [
      {
        id: 'maint1',
        question: "How do I maintain my solar system?",
        answer: "Solar systems require minimal maintenance. We recommend cleaning panels every 6-12 months (more in dusty areas), checking connections annually, and monitoring system performance. We offer maintenance packages for your convenience."
      },
      {
        id: 'maint2',
        question: "What happens during a power outage?",
        answer: "Off-grid systems continue working normally. Grid-tied systems without batteries automatically shut down for safety. Systems with battery backup provide uninterrupted power during outages."
      },
      {
        id: 'maint3',
        question: "What warranty do you offer?",
        answer: "We provide: 25-year panel performance warranty, 10-year inverter warranty, 5-year workmanship warranty, and 2-year free maintenance. Extended warranties are available for additional peace of mind."
      },
      {
        id: 'maint4',
        question: "How do I monitor my system's performance?",
        answer: "We provide monitoring apps that show real-time energy production, consumption, and system health on your smartphone or computer. Our team also receives alerts for any performance issues."
      }
    ],
    technical: [
      {
        id: 'tech1',
        question: "What's the difference between on-grid and off-grid systems?",
        answer: "On-grid systems connect to the national grid, allowing you to sell excess power. Off-grid systems are independent with battery storage. Hybrid systems combine both features for maximum flexibility."
      },
      {
        id: 'tech2',
        question: "How do I determine the right system size?",
        answer: "We analyze your electricity bills, assess your energy needs, consider future consumption, and evaluate your site conditions to recommend the optimal system size for maximum efficiency and cost-effectiveness."
      },
      {
        id: 'tech3',
        question: "What are solar batteries and do I need them?",
        answer: "Solar batteries store excess energy for use when the sun isn't shining. They're essential for off-grid systems and recommended for areas with frequent power outages. We offer various battery options based on your needs."
      },
      {
        id: 'tech4',
        question: "Can solar power heavy appliances like air conditioners?",
        answer: "Yes, with proper system sizing. We design systems to handle specific loads, including air conditioners, water pumps, and industrial machinery. Our energy audit identifies all your power requirements."
      }
    ]
  };

  const categories = [
    { id: 'general', name: 'General Questions', icon: <FaQuestionCircle />, count: faqData.general.length },
    { id: 'installation', name: 'Installation', icon: <FaTools />, count: faqData.installation.length },
    { id: 'financial', name: 'Financial', icon: <FaMoneyBillWave />, count: faqData.financial.length },
    { id: 'maintenance', name: 'Maintenance', icon: <FaShieldAlt />, count: faqData.maintenance.length },
    { id: 'technical', name: 'Technical', icon: <FaSolarPanel />, count: faqData.technical.length }
  ];

  return (
    <section id="faq" className="faq">
      <div className="container">
        <div className="section-title">
          <h2>Frequently Asked Questions</h2>
          <p>Find answers to common questions about solar energy solutions in Malawi</p>
        </div>

        <div className="faq-container">
          {/* Category Navigation */}
          <div className="faq-categories">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <span className="category-icon">{category.icon}</span>
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.count}</span>
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="faq-content">
            <div className="faq-header">
              <h3>
                {categories.find(c => c.id === activeCategory)?.icon}
                {categories.find(c => c.id === activeCategory)?.name}
              </h3>
              <p className="faq-subtitle">
                {activeCategory === 'general' && 'Basic information about solar energy'}
                {activeCategory === 'installation' && 'Installation process and requirements'}
                {activeCategory === 'financial' && 'Costs, ROI, and financing options'}
                {activeCategory === 'maintenance' && 'System care and warranty information'}
                {activeCategory === 'technical' && 'Technical specifications and system details'}
              </p>
            </div>

            <div className="faq-list">
              {faqData[activeCategory].map(item => (
                <div 
                  key={item.id} 
                  className={`faq-item ${openQuestions.includes(item.id) ? 'active' : ''}`}
                >
                  <div 
                    className="faq-question"
                    onClick={() => toggleQuestion(item.id)}
                  >
                    <h4>{item.question}</h4>
                    <span className="faq-toggle">
                      {openQuestions.includes(item.id) ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                  <div className="faq-answer">
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Help Section */}
            <div className="faq-help">
              <div className="help-card">
                <h4>Still have questions?</h4>
                <p>Can't find the answer you're looking for? Our solar experts are here to help.</p>
                <div className="help-buttons">
                  <a href="#contact" className="btn">
                    <FaQuestionCircle /> Contact Us
                  </a>
                  <a href="tel:+265888615918" className="btn btn-outline">
                    <FaTools /> Call Now: +265888615918
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add this after the help section, before the closing container div */}
<div className="faq-stats">
  <div className="faq-stat-item">
    <h3>500+</h3>
    <p>Systems Installed</p>
  </div>
  <div className="faq-stat-item">
    <h3>25+</h3>
    <p>Years Combined Experience</p>
  </div>
  <div className="faq-stat-item">
    <h3>98%</h3>
    <p>Customer Satisfaction</p>
  </div>
  <div className="faq-stat-item">
    <h3>24/7</h3>
    <p>Technical Support</p>
  </div>
</div>
    </section>
  );
};

export default FAQ;